/**
 * Missions judged by the clock (mission 11): what he should be doing right now about the signal,
 * the noise he owes it, and the border. Found in play: a ranger standing AT the border reading
 * [0/1], Recall dead under his thumb, and nothing on screen to say that both are how it is meant
 * to be. Pure: text from the mission definition and the objective state.
 */

import type { MissionDef, ObjectiveRule, World } from '../../sim/types';
import { TICK_RATE } from '../../sim/types';

const signalRule = (def: MissionDef): Extract<ObjectiveRule, { type: 'signal' }> | undefined =>
  def.objective.rules.find((r): r is Extract<ObjectiveRule, { type: 'signal' }> => r.type === 'signal');

const mmss = (seconds: number): string => `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;

export function standingLine(def: MissionDef, world: World): { text: string; warn: boolean } | null {
  const o = world.objective;
  if (o.failed || o.complete) return null;
  const signal = signalRule(def);
  if (signal && !o.signalled) return { text: 'LIE LOW — WAIT FOR THE SIGNAL', warn: false };
  if (signal && !o.wentLoud) {
    const left = Math.max(0, Math.ceil(signal.failIfQuietForSeconds - o.quietTicks / TICK_RATE));
    return { text: `THE SIGNAL HAS GONE — MAKE NOISE NOW ${mmss(left)}`, warn: true };
  }
  const zone = def.objective.kind === 'reachZone' ? def.objective.zone : undefined;
  if (!zone) return null;
  const r = world.ranger;
  const inside = r.x >= zone.x0 && r.x <= zone.x1 && r.z >= zone.z0 && r.z <= zone.z1;
  return inside
    ? { text: 'IN POSITION — HOLD HERE UNTIL THE CLOCK RUNS OUT', warn: false }
    : { text: 'BE AT THE BORDER WHEN THE CLOCK RUNS OUT — NO RECALL', warn: false };
}

/** The signal beep, in words: the first is the signal itself, the second the 30-second warning. */
export function signalText(def: MissionDef, world: World): string | null {
  const o = world.objective;
  if (!signalRule(def) || o.wentLoud || o.failed) return null;
  return o.quietTicks < TICK_RATE
    ? 'THE SIGNAL — MAKE NOISE NOW: SHOTS, GRENADES, ANYTHING LOUD'
    : '30 SECONDS TO MAKE NOISE OR THE DIVERSION FAILS';
}
